import React, { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import useScrollMotion from "../hooks/useScrollMotion";
import AnimatedText from "./AnimatedText";
import { SITE } from "../mock";

/**
 * Cinematic auto-advancing hero slideshow with crossfade + Ken Burns motion.
 * Includes manual prev/next, slide-dot indicators, and a scroll cue.
 */
const CinematicSlide = ({
  slides = [],
  eyebrow = "San Juan del Sur · Nicaragua",
  heading,
  headingLines,
  body,
  intervalMs = 6500,
}) => {
  const [idx, setIdx] = useState(0);
  useScrollMotion();

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(t);
  }, [slides.length, intervalMs]);

  const next = () => setIdx((i) => (i + 1) % slides.length);
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);

  const motions = ["kb-zoom-in", "kb-pan-right", "kb-zoom-out", "kb-pan-left", "kb-zoom-in", "kb-pan-right"];

  const whatsappLink = `https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
    "Hi Casa Elio! I'd like to book a stay in San Juan del Sur. Could you share availability and rates?"
  )}`;

  return (
    <section className="relative h-[88svh] min-h-[560px] md:h-screen md:min-h-[720px] w-full overflow-hidden bg-black">
      {/* Slides */}
      <div className="absolute inset-0">
        {slides.map((s, i) => {
          const motion = motions[i % motions.length];
          const isActive = i === idx;
          return (
            <div
              key={s.src + i}
              className={`absolute inset-0 transition-opacity duration-[1600ms] ease-[cubic-bezier(.4,.0,.2,1)] ${
                isActive ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <div
                className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${motion} ${isActive ? "motion-active" : ""}`}
                style={{ backgroundImage: `url(${s.src})` }}
              />
            </div>
          );
        })}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/80 z-20" />
      </div>

      {/* Copy — eyebrow → heading → buttons → body */}
      <div className="relative z-30 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-32 text-[#f5f0e6]">
        <div className="reveal reveal-slow">
          <div className="reveal text-[10px] md:text-[11px] uppercase tracking-eyebrow opacity-90 mb-5 md:mb-6">
            {eyebrow}
          </div>
          <h1 className="font-serif-display text-[34px] sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.08] md:leading-[1.06] max-w-4xl drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)]">
            {headingLines && headingLines.length > 0 ? (
              headingLines.map((line, idx) => (
                <span key={idx} className="block">
                  <AnimatedText
                    text={line.text}
                    italic={!!line.italic}
                    stagger={26}
                    baseDelay={idx === 0 ? 250 : 250 + (headingLines[0]?.text?.length || 0) * 26 + 120}
                    className={line.italic ? "opacity-90" : ""}
                  />
                </span>
              ))
            ) : (
              heading
            )}
          </h1>

          {/* Booking buttons — directly under the heading */}
          <div className="mt-8 md:mt-10 flex flex-col w-full sm:max-w-xs gap-3">
            <a
              href={SITE.airbnbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-3 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Airbnb
              <ArrowRight size={14} />
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 text-[12px] uppercase tracking-[0.3em] px-7 py-4 bg-[#f5f0e6] text-[#1f2a24] hover:bg-white transition-colors"
            >
              <MessageCircle size={14} />
              Book Now
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-3 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Booking.com
              <ArrowRight size={14} />
            </a>
          </div>

          {/* Body copy — types in AFTER the heading finishes */}
          <p className="mt-7 md:mt-9 max-w-xl text-[14px] md:text-lg leading-relaxed opacity-90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.3)]">
            <AnimatedText
              text={body || ""}
              stagger={14}
              baseDelay={
                250 +
                ((headingLines?.[0]?.text?.length || 0) +
                  (headingLines?.[1]?.text?.length || 0)) * 26 +
                420
              }
            />
          </p>
        </div>
      </div>

      {/* Slide indicators + manual nav */}
      <div className="absolute z-30 bottom-7 right-6 md:right-10 flex items-center gap-3 text-[#f5f0e6]">
        <button onClick={prev} aria-label="Previous slide" className="hidden md:flex h-9 w-9 items-center justify-center border border-[#f5f0e6]/40 hover:bg-[#f5f0e6]/15 transition-colors">
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="group relative h-[3px] w-9 md:w-12 bg-[#f5f0e6]/25 overflow-hidden"
            >
              <span
                className={`absolute inset-y-0 left-0 bg-[#f5f0e6] ${i === idx ? "animate-[slideprogress_6.5s_linear_forwards]" : "w-0"}`}
                style={i < idx ? { width: "100%" } : undefined}
              />
            </button>
          ))}
        </div>
        <button onClick={next} aria-label="Next slide" className="hidden md:flex h-9 w-9 items-center justify-center border border-[#f5f0e6]/40 hover:bg-[#f5f0e6]/15 transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Scroll cue */}
      <div className="hidden md:flex absolute z-30 bottom-8 left-1/2 -translate-x-1/2 text-[#f5f0e6] flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.4em] opacity-80">Scroll</span>
        <div className="relative w-px h-12 bg-[#f5f0e6]/30 overflow-hidden">
          <div className="absolute inset-0 bg-[#f5f0e6] scroll-line" />
        </div>
      </div>
    </section>
  );
};

export default CinematicSlide;
